import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { catchError, concatMap, map, mergeMap, take, tap } from 'rxjs/operators';

import { CustomerActions } from '../actions';
import { CustomerFacade } from '../facades/customer.facade';
import { EAddressType, EGlueResource, EStoreFeatures } from '../configurations/common';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { CpqFacade } from '../facades/cpq.facade';
import { SoldToAccountsFacade } from '../facades/sold-to.facade';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { WindowService } from '../services/window.service';

@Injectable()
export class CustomerEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly cpqFacade: CpqFacade = inject(CpqFacade);
  private readonly soldToAccountsFacade: SoldToAccountsFacade = inject(SoldToAccountsFacade);
  private readonly windowService: WindowService = inject(WindowService);

  readonly initCustomerAddressesAfterLogin$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.CompanyUsersActionSuccess),
      mergeMap(() => {
        this.customerFacade.beginGetCustomerAddressesAction();
        if (this.configurationFacade.isFeatureAvailable(EStoreFeatures.MY_PROFILE)) {
          return this.customerFacade.isBusinessPartner()
            .pipe(
              take(1),
              concatMap(isBp => {
                if (isBp) {
                  return [
                    CustomerActions.loadCustomerSoldToAddresses(),
                  ];
                } else {

                  const lang: string = LocalStorageUtils.getKeyValue('lang');
                  const langData: string = LocalStorageUtils.getKeyValue(`lang-data-${lang}`);

                  if (langData === '') {
                    return [];
                  } else {
                    return [
                      CustomerActions.loadCustomerShipToAddresses(),
                    ];
                  }
                }
              }),
            );
        } else {
          return [];
        }
      }),
    );
  });

  notifyMedalliaAfterLogin$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.CompanyUsersActionSuccess),
      tap(() => {
        const w: any = this.windowService.nativeWindow;
        if (w?.KAMPYLE_ONSITE_SDK?.updatePageView) {
          w.KAMPYLE_ONSITE_SDK.updatePageView();
        }
      }),
    );
  }, {dispatch: false});

  readonly loadCustomerSoldToAddresses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.loadCustomerSoldToAddresses),
      mergeMap(() => this.soldToAccountsFacade.getSoldToAccounts([EGlueResource.SHIP_TO_ADDRESSES])
        .pipe(
          take(1),
          map(response => CustomerActions.loadCustomerSoldToAddressesSuccess({payload: response})),
          catchError(error => of(CustomerActions.loadCustomerSoldToAddressesError(error))),
        ),
      ),
    );
  });

  readonly loadCustomerShipToAddresses$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.loadCustomerShipToAddresses),
      mergeMap(() => this.cpqFacade.getCpqWrongAddress(EAddressType.SHIP_TO)
        .pipe(
          take(1),
          map(response => CustomerActions.loadCustomerShipToAddressesSuccess({payload: response})),
          catchError(error => of(CustomerActions.loadCustomerShipToAddressesError(error))),
        )),
    );
  });

  readonly getBusinessUnits$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.startGetBusinessUnitAction),
      mergeMap(
        () => this.customerFacade.getBusinessUnits()
          .pipe(
            map(data => {
              return CustomerActions.successGetBusinessUnitAction({payload: data});
            }),
            catchError(error => of(CustomerActions.errorGetBusinessUnitAction(error)),
            ),
          ),
      ),
    );
  });

  readonly getCustomerAddress$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.beginGetCustomerAddressAction),
      mergeMap(
        (action) => this.customerFacade.getCustomerAddresses(JSON.stringify(action.payload))
          .pipe(
            map(data => {
              return CustomerActions.successGetCustomerAddressAction({payload: data});
            }),
            catchError(error => of(CustomerActions.errorGetCustomerAddressAction(error)),
            ),
          ),
      ),
    );
  });

  readonly addCustomerAddress$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.beginAddCustomerAddressAction),
      mergeMap(
        (action) => this.customerFacade.addCustomerAddress(action.customerId, action.data)
          .pipe(
            map(response => {
              return CustomerActions.successAddCustomerAddressAction({data: response});
            }),
            catchError(error => of(CustomerActions.errorAddCustomerAddressAction(error)),
            ),
          ),
      ),
    );
  });

  readonly updateCustomerAddress$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.beginUpdateCustomerAddressAction),
      mergeMap(
        (action) => this.customerFacade.updateCustomerAddress(action.customerId, action.addressId, action.data)
          .pipe(
            map(response => {
              return CustomerActions.successUpdateCustomerAddressAction({data: response});
            }),
            catchError(error => of(CustomerActions.errorUpdateCustomerAddressAction(error)),
            ),
          ),
      ),
    );
  });

  readonly deleteCustomerAddress$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.beginDeleteCustomerAddressAction),
      mergeMap((action) => {
        return this.customerFacade.deleteCustomerAddress(action.customerId, action.addressId).pipe(
          map(() => {
            if (action.isPreferredShipToAddress) {
              return CustomerActions.beginDeletedPreferredShipToAddressRemovalAction({
                customerId: action.customerId,
                addressId: action.addressId,
              });
            } else {
              return CustomerActions.successDeleteCustomerAddressAction({
                customerId: action.customerId,
                addressId: action.addressId,
              });
            }
          }),
          catchError(error => of(CustomerActions.errorDeleteCustomerAddressAction(error))),
        );
      }),
    );
  });

  readonly removeDeletedPreferredShipToAddress$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.beginDeletedPreferredShipToAddressRemovalAction),
      mergeMap(
        (action) => {
          const data: any = {
            data: {
              type: 'customer-preferences',
              attributes: {
                preferredShipToId: '',
              },
            },
          };

          return this.customerFacade.saveCustomerSettingPreferences(action.customerId, data).pipe(
            map(response => {
              return CustomerActions.successDeletedPreferredShipToAddressRemovalAction({
                customerId: action.customerId,
                addressId: action.addressId,
                data: response,
              });
            }),
            catchError(error => of(CustomerActions.errorDeletedPreferredShipToAddressRemovalAction(error))),
          );
        },
      ),
    );
  });
}
